import type { FC, ReactNode } from "react";
import { useMatch } from "react-router";
import { ColoredLabel, formatDateTimeFromISO, Tag, type TagIntent } from "widgets";

import { getHitAccessionVersion } from "@/app-utils/blast.js";
import { getTagIntentColor } from "@/app-utils/events.js";
import { toTitleCase } from "@/app-utils/string.ts";
import type { BlastJob, JobResult } from "@/graphql.js";
import { matchStatusToTagIntent } from "@/modules/jobs/utils/statusToTag.ts";
import { ModulesIcons } from "@/settings/assets-settings.js";

import { AttributesListWidget, type AttributeWidgetProps } from "../core/navigation-panel/AttributeWidget.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";

export interface IBlastPageHeader {
  job: BlastJob;
  jobResult?: JobResult;
  subtitle?: string;
  actionWidgets?: ReactNode[];
  toolsWidgets?: ReactNode[];
  isLoading?: boolean;
}

export const defaultBlastHeaderAttributes = (job: BlastJob) => {
  const attributes: AttributeWidgetProps[] = [];

  attributes.push({
    label: "Created",
    description: formatDateTimeFromISO(job.created),
  });

  if (job.tool) {
    attributes.push({
      label: "Method",
      description: <Tag key={"blast-page-header-tag-2"}>{job.tool.toUpperCase()}</Tag>,
    });
  }

  attributes.push({
    label: "Status",
    description: (
      <ColoredLabel
        color={getTagIntentColor(matchStatusToTagIntent(job.status).intentColor as TagIntent)}
        key={"blast-page-header-tag-1"}
        label={toTitleCase(job.status)}
      />
    ),
  });

  return attributes;
};

const BlastPageHeader: FC<IBlastPageHeader> = ({ job, jobResult, subtitle, actionWidgets, toolsWidgets, ...props }) => {
  const hitNumMatch = useMatch("/job/:jobId/results/:queryId/hitdetails/:hitNum/*");

  if (!job || props.isLoading) {
    return <PageHeader title={""} isLoading project={job.event?.project} />;
  }

  const hitNum = (hitNumMatch?.params.hitNum && Number.parseInt(hitNumMatch?.params.hitNum, 10)) || null;
  const headerAttributes: AttributeWidgetProps[] = [];

  if (jobResult?.blastHits) {
    if (hitNum) {
      // hit attributes
      const hit = jobResult.blastHits[hitNum - 1];
      headerAttributes.push({ label: "Accession", description: getHitAccessionVersion(hit) });
    } else {
      // query attributes
      headerAttributes.push({ label: "Length", description: jobResult.query?.length || "unknown" });
    }
  } else {
    headerAttributes.push(...defaultBlastHeaderAttributes(job));
  }

  return (
    <PageHeader
      title={
        jobResult && jobResult.query
          ? `${jobResult.query.accession}.${jobResult.query.version}`
          : job.eventName || job.name
      }
      subtitle={jobResult && jobResult.query ? jobResult.query.description : subtitle}
      project={job.event?.project}
      setDocumentTitle
      tags={[<img src={ModulesIcons.BLAST.large} width={77} height={44} key={"blast-summary-view-tag-1"} />]}
      widgets={[<AttributesListWidget attributes={headerAttributes} key={"blast-page-widget-1"} />]}
      actionWidgets={actionWidgets}
      toolsWidgets={toolsWidgets}
    />
  );
};

export default BlastPageHeader;
